import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { Button, Typography } from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { configureAuth } from '../../authConfig'
import logger from '../../app/middleware/log'
import RoutesEnum from '../../components/Routes/rootEnum'
import { POST_LOGIN_DEST_KEY, toSafeInternalPath } from './authRedirect'
import { fetchIdentityConfig } from './IdentityConfigService'
import InvalidWorkspacePage from './InvalidWorkspacePage'
import PlatformBrandedShell from './PlatformBrandedShell'

type LoadState = 'loading' | 'invalid' | 'error'

// E7 F7-5.1: defers MSAL construction until GET /identity-config resolves, because MSAL can't be
// reconfigured after construction and the authority/clientId/scope are per-Tenant. 404 → the workspace
// isn't recognised (InvalidWorkspacePage); a populated config builds + initialises MSAL and wraps the
// app in MsalProvider. The AUTH_DISABLED docker-dev path never reaches this (index.tsx short-circuits).
const IdentityConfigLoader = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<LoadState>('loading')
  const [instance, setInstance] = useState<PublicClientApplication | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const config = await fetchIdentityConfig()
      if (cancelled) {
        return
      }
      if (config === null) {
        setState('invalid')
        return
      }

      const msal = new PublicClientApplication(configureAuth(config))
      // MSAL v3+ requires initialize() before any other API call.
      await msal.initialize()

      // Process the redirect response HERE, before MsalProvider runs its own handleRedirectPromise, with
      // navigateToLoginRequestUrl:false. Otherwise MSAL navigates the browser back to the page that started
      // the sign-in (e.g. /login) instead of leaving us on the dedicated /auth/callback route — which
      // stranded the user on the Sign In page. In MSAL v5 this flag is a handleRedirectPromise option (no
      // longer on the global auth config). Consuming the response here means AuthCallbackPage can't catch
      // the ACQUIRE_TOKEN_SUCCESS event, so the post-login destination is handed to it via sessionStorage.
      // A rejected redirect (user cancels, consent denied, an AADSTS error) is an EXPECTED auth outcome, not
      // an app failure — catch it locally and route to /login with an error flag rather than letting it hit
      // the outer catch, which shows the generic "temporarily unavailable" page.
      try {
        const redirectResult = await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false })
        if (cancelled) {
          return
        }
        if (redirectResult?.account) {
          msal.setActiveAccount(redirectResult.account)
          sessionStorage.setItem(POST_LOGIN_DEST_KEY, toSafeInternalPath(redirectResult.state) ?? '')
        }
      } catch (redirectError) {
        if (cancelled) {
          return
        }
        logger.error('Redirect sign-in failed:', redirectError)
        sessionStorage.setItem(POST_LOGIN_DEST_KEY, `${RoutesEnum.LOGIN}?error=login_failed`)
      }
      if (!msal.getActiveAccount() && msal.getAllAccounts().length > 0) {
        msal.setActiveAccount(msal.getAllAccounts()[0])
      }
      if (!cancelled) {
        setInstance(msal)
      }
    }

    load().catch((error) => {
      // Identity-config fetch failure (network/5xx) or MSAL init failure (corrupt cache, blocked
      // storage). Surface a recoverable error rather than a permanent blank page.
      logger.error('Identity-config bootstrap failed:', error)
      if (!cancelled) {
        setState('error')
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  if (state === 'invalid') {
    return <InvalidWorkspacePage />
  }

  // These boot states render OUTSIDE the Intl/Theme providers (index.tsx boots this above them), so they
  // use plain strings and MUI's default theme. Platform-branded so they match /login and /signup.
  if (state === 'error') {
    return (
      <PlatformBrandedShell>
        <Typography variant='h5' component='h1' textAlign='center'>
          Sign-in is temporarily unavailable
        </Typography>
        <Typography variant='body2' color='text.secondary' textAlign='center'>
          We couldn&apos;t load this workspace&apos;s configuration. This can happen on a network blip or if
          browser storage is blocked.
        </Typography>
        <Button variant='contained' onClick={() => { sessionStorage.clear(); window.location.reload() }}>
          Clear local data and retry
        </Button>
      </PlatformBrandedShell>
    )
  }

  if (instance === null) {
    return (
      <PlatformBrandedShell>
        <Typography variant='body2' color='text.secondary' textAlign='center'>
          Loading your workspace…
        </Typography>
      </PlatformBrandedShell>
    )
  }

  return <MsalProvider instance={instance}>{children}</MsalProvider>
}

export default IdentityConfigLoader
